import { useCallback, useMemo } from 'react';

import { useAppSelector } from '@verantos-dev/app-state-base';
import {
  selectIsSaving,
  useCohortDefinitionStore,
} from '@verantos-dev/app-state-cohort-definition';
import {
  selectResearchAssistantIsReady,
  selectResearchAssistantMessages,
  selectResearchAssistantModel,
  selectResearchAssistantProgressState,
  useAllowMessageSubmit,
  useIsWaitingForResponse,
} from '@verantos-dev/app-state-research-assistant';
import { useAuth } from '@verantos-dev/core-auth';
import { useCohortBuilderData } from '@verantos-dev/data-access-cohorts';
import { PROGRESS_STEP_MESSAGES } from '@verantos-dev/types-common';

import { useResearchAssistantPromptState } from '../components/ResearchAssistantChatWindow/useResearchAssistantPromptState';
import { useScrollMessageIntoView } from '../components/ResearchAssistantChatWindow/useScrollMessageIntoView';
import { useSuggestedActions } from '../components/ResearchAssistantChatWindow/useSuggestedActions';
import { useFeedback } from '../hooks/useFeedback';

/**
 * Container hook that manages all side effects for the Research Assistant
 * This replaces the logic from useResearchAssistantChatWindow but in a container pattern
 */
interface UseResearchAssistantContainerParams {
  isOwner: boolean;
}

export const useResearchAssistantContainer = ({ isOwner }: UseResearchAssistantContainerParams) => {
  const isWaitingForResponse = useIsWaitingForResponse();
  const isSocketReady = useAppSelector(selectResearchAssistantIsReady);
  const allowMessageSubmit = useAllowMessageSubmit();
  // Combine socket readiness with cohort-status permission so downstream
  // ChatWindowInput disables the send button during PROCESSING/FINAL.
  const isReady = isSocketReady && allowMessageSubmit !== false;
  const inputPlaceholder =
    isSocketReady && allowMessageSubmit === false
      ? 'Messaging is disabled while the cohort is being processed.'
      : undefined;
  const model = useAppSelector(selectResearchAssistantModel);
  const messages = useAppSelector(selectResearchAssistantMessages);
  const progressState = useAppSelector(selectResearchAssistantProgressState);

  // Auth context for user initials
  const {
    user: { userInitials },
  } = useAuth();
  const { data: cohortData } = useCohortBuilderData();
  const isSaving = useCohortDefinitionStore(selectIsSaving);

  const cohortOwnerInitials = useMemo(() => {
    const firstName = cohortData?.data?.createdBy?.firstName?.trim();
    const lastName = cohortData?.data?.createdBy?.lastName?.trim();

    if (!firstName && !lastName) {
      return undefined;
    }

    const firstInitial = firstName?.charAt(0)?.toUpperCase() ?? '';
    const lastInitial = lastName?.charAt(0)?.toUpperCase() ?? '';

    return `${firstInitial}${lastInitial}`.trim() || undefined;
  }, [cohortData?.data?.createdBy?.firstName, cohortData?.data?.createdBy?.lastName]);

  const messagesWithInitials = useMemo(() => {
    if (!messages) {
      return [];
    }

    return messages.map((message) => {
      const sanitizedMessageInitials =
        typeof message.userInitials === 'string' && message.userInitials.trim().length > 0
          ? message.userInitials.trim()
          : undefined;
      const fallbackOwnerInitials =
        !message.isCurrentUser && message.currentUser === 'user' ? cohortOwnerInitials : undefined;

      return {
        ...message,
        userInitials: sanitizedMessageInitials ?? fallbackOwnerInitials,
      };
    });
  }, [messages, cohortOwnerInitials]);

  const chatWindowUserInitials = isOwner ? userInitials : cohortOwnerInitials ?? userInitials;

  // Scroll management
  const { chatWindowRef, messagesEndRef } = useScrollMessageIntoView();

  // Prompt state management
  const {
    userPrompt,
    onModelChange,
    onUserPromptChange,
    sendUserPrompt,
    fileUploadConfig,
    fileUploadDragDropTexts,
  } = useResearchAssistantPromptState();

  // Suggested actions with feature flag handling
  const suggestedActions = useSuggestedActions({ onUserPromptChange });

  // Use the comprehensive progress message mapping from types-cohorts
  const progressMessageMap = PROGRESS_STEP_MESSAGES;

  // Feedback handling
  const { feedbackState, submitFeedback, deleteFeedback } = useFeedback();

  // Guard send against saving — user can still type, but send is blocked until sync completes
  const guardedSendUserPrompt = useCallback(() => {
    if (isSaving) return;
    sendUserPrompt();
  }, [isSaving, sendUserPrompt]);

  return {
    // State
    isWaitingForResponse: isWaitingForResponse || isSaving,
    isReady,
    inputPlaceholder,
    model,
    messages: messagesWithInitials,
    progressState,

    // Refs
    chatWindowRef,
    messagesEndRef,

    // Actions
    userPrompt,
    onModelChange,
    onUserPromptChange,
    sendUserPrompt: guardedSendUserPrompt,
    suggestedActions,
    fileUploadConfig,
    fileUploadDragDropTexts,
    // User context
    userInitials,
    ownerInitials: cohortOwnerInitials,
    chatWindowUserInitials,

    // Configuration
    progressMessageMap,

    // Feedback
    feedbackState,
    submitFeedback,
    deleteFeedback,
  };
};
